import { createThunklessAction } from 'thunkless';
import _ from 'lodash';

import { TicketStatusOption } from '@/constants/ticket-status-option.enum';
import { avo } from '@/api/app/avo-client';
import * as UUID from '@/lib/uuid';

import {
  setStatusOfTicketStatusSelector, ticketStatusSelector,
  ticketSetUnaddressedStatusSelector,
  setOwnerOfTicketStatusSelector, ticketUserIdSelector, ticketFetchPreviousTicketsStatusSelector,
} from '../selectors/ticket';
import { replaceUri } from './navigation';

export enum TicketTypes {
  FETCH_TICKET_REQUEST = 'app/ticket/FETCH_TICKET_REQUEST',
  FETCH_TICKET_SUCCESS = 'app/ticket/FETCH_TICKET_SUCCESS',
  FETCH_TICKET_FAILURE = 'app/ticket/FETCH_TICKET_FAILURE',

  CREATE_NOTE_REQUEST = 'app/ticket/CREATE_NOTE_REQUEST',
  CREATE_NOTE_SUCCESS = 'app/ticket/CREATE_NOTE_SUCCESS',
  CREATE_NOTE_FAILURE = 'app/ticket/CREATE_NOTE_FAILURE',

  SET_UNADDRESSED_REQUEST = 'app/ticket/SET_UNADDRESSED_REQUEST',
  SET_UNADDRESSED_SUCCESS = 'app/ticket/SET_UNADDRESSED_SUCCESS',
  SET_UNADDRESSED_FAILURE = 'app/ticket/SET_UNADDRESSED_FAILURE',

  SET_STATUS_REQUEST = 'app/ticket/SET_STATUS_REQUEST',
  SET_STATUS_SUCCESS = 'app/ticket/SET_STATUS_SUCCESS',
  SET_STATUS_FAILURE = 'app/ticket/SET_STATUS_FAILURE',

  SET_OWNER_REQUEST = 'app/ticket/SET_OWNER_REQUEST',
  SET_OWNER_SUCCESS = 'app/ticket/SET_OWNER_SUCCESS',
  SET_OWNER_FAILURE = 'app/ticket/SET_OWNER_FAILURE',

  FETCH_PREVIOUS_TICKETS_REQUEST = 'app/ticket/FETCH_PREVIOUS_TICKETS_REQUEST',
  FETCH_PREVIOUS_TICKETS_SUCCESS = 'app/ticket/FETCH_PREVIOUS_TICKETS_SUCCESS',
  FETCH_PREVIOUS_TICKETS_FAILURE = 'app/ticket/FETCH_PREVIOUS_TICKETS_FAILURE',

  TOGGLE_PREVIOUS_TICKETS = 'app/ticket/TOGGLE_PREVIOUS_TICKETS',
}

export const fetchTicket = (subdomain: string, uuid: string) => createThunklessAction({
  type: [
    TicketTypes.FETCH_TICKET_REQUEST,
    TicketTypes.FETCH_TICKET_SUCCESS,
    TicketTypes.FETCH_TICKET_FAILURE,
  ] as const,
  promise: () => avo.ajax<{ tickets: any[] }>({ route: `/v1/tickets/${uuid}`, params: { subdomain, by_uuid: 'true' } })
    .then((response) => {
      response.data.tickets.forEach((t) => _.update(t, ['s3_direct_post', 'formdata'], JSON.parse));
      return response;
    }),
  dispatchOnError: () => replaceUri({ path: `/accounts/${subdomain}/tickets` }),
  statusSelector: (state) => state.ticket.loadingStatus[uuid],
  meta: { uuid },
});

export interface CreateNoteParams {
  note: string,
  subdomain: string,
  ticket: string,
  callId?: string;
  senderTag: string;
  temporaryId?: string;
}
export const createNote = ({
  note, subdomain, ticket, callId, senderTag, temporaryId = UUID.generate(),
}: CreateNoteParams) =>
  createThunklessAction({
    type: [
      TicketTypes.CREATE_NOTE_REQUEST,
      TicketTypes.CREATE_NOTE_SUCCESS,
      TicketTypes.CREATE_NOTE_FAILURE,
    ] as const,
    promise: () => avo.ajax<{ tickets: any[] }>({
      method: 'POST',
      route: `/accounts/${subdomain}/tickets/${ticket}/avo_notes`,
      payload: {
        'avo_note[body]': note,
        'avo_note[call_id]': callId,
        temporary_id: temporaryId,
      },
    }),
    meta: { ticket, callId, temporaryId, note, senderTag },
  });

export const setUnaddressed = (subdomain: string, ticket: string, unaddressed: boolean) =>
  createThunklessAction({
    type: [
      TicketTypes.SET_UNADDRESSED_REQUEST,
      TicketTypes.SET_UNADDRESSED_SUCCESS,
      TicketTypes.SET_UNADDRESSED_FAILURE,
    ] as const,
    promise: () => avo.ajax<void>({ method: 'POST', route: `/accounts/${subdomain}/tickets/${ticket}/update_unaddressed`, payload: { unaddressed } }),
    statusSelector: _.partial(ticketSetUnaddressedStatusSelector, ticket),
    meta: { subdomain, ticket, unaddressed },
  });

export const setTicketStatus = (subdomain: string, ticket: string, status: TicketStatusOption) =>
  createThunklessAction({
    type: [
      TicketTypes.SET_STATUS_REQUEST,
      TicketTypes.SET_STATUS_SUCCESS,
      TicketTypes.SET_STATUS_FAILURE,
    ] as const,
    promise: () => avo.ajax<void>({ method: 'PUT', route: `/accounts/${subdomain}/tickets/${ticket}/update_status`, payload: { status } }),
    statusSelector: _.partial(setStatusOfTicketStatusSelector, ticket),
    transform: (action, state) => (
      action.meta ? action : Object.assign(action, {
        meta: {
          subdomain, ticket, currentStatus: ticketStatusSelector(ticket, state), newStatus: status,
        },
      })
    ),
    meta: null as { subdomain: string, ticket: string, currentStatus: string, newStatus: string },
  });

export const setTicketOwner = (subdomain: string, ticket: string, user_id: string) =>
  createThunklessAction({
    type: [
      TicketTypes.SET_OWNER_REQUEST,
      TicketTypes.SET_OWNER_SUCCESS,
      TicketTypes.SET_OWNER_FAILURE,
    ] as const,
    promise: () => avo.ajax<void>({ method: 'PUT', route: `/accounts/${subdomain}/tickets/${ticket}/update_owner`, payload: { user_id } }),
    statusSelector: _.partial(setOwnerOfTicketStatusSelector, ticket),
    transform: (action, state) => (
      action.meta ? action : Object.assign(action, {
        meta: { ticket, currentOwner: ticketUserIdSelector(ticket, state), newOwner: user_id },
      })
    ),
    meta: null as { ticket: string, currentOwner: string, newOwner: string },
  });

export interface FetchPreviousTicketsParams {
  ticketUuid: string;
  ticketId: string;
  subdomain: string;
}
export const fetchPreviousTickets =
  ({ ticketUuid, ticketId, subdomain }: FetchPreviousTicketsParams) => createThunklessAction({
    type: [
      TicketTypes.FETCH_PREVIOUS_TICKETS_REQUEST,
      TicketTypes.FETCH_PREVIOUS_TICKETS_SUCCESS,
      TicketTypes.FETCH_PREVIOUS_TICKETS_FAILURE,
    ] as const,
    promise: () => avo.ajax<any>({ route: `/v1/tickets/${ticketId}/previous`, params: { subdomain } }),
    statusSelector: _.partial(ticketFetchPreviousTicketsStatusSelector, ticketUuid),
    meta: { ticket: ticketUuid },
  });

export const togglePreviousTickets = (ticket: string) => ({
  type: TicketTypes.TOGGLE_PREVIOUS_TICKETS as const,
  meta: { ticket },
});
